import React from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { pageRoleLabel } from "../../Common/PhotoBook";
import "./Modal.css";

// Photo Book preview viewer (project plan: "Photo Book: page-flip preview") —
// replaces the old "Generate PDF (preview)" button, which handed out a
// direct link to the exact full-resolution, print-ready PDF (a real
// revenue leak: anyone could save and print that file anywhere, bypassing
// the paid order entirely). This viewer only ever receives already-
// screen-resolution images (resolvePreviewImageSrc in PhotoBook.js, not the
// print-quality resolvePageImageSrc) — the print-resolution file is never
// generated or exposed until an actual order starts.
//
// Plain page/spread viewer, no page-flip library (2026-10-08). It used
// react-pageflip, which caused a run of layout bugs we could only work
// around from outside: the dialog shrank and the cover moved on every turn
// (its book height tracked the container width, and the curling page made a
// scrollbar flash on), the cover first drew too high in single-page mode
// (stale first measurement while the modal animated open), and Next seemed
// to stop after page 2 in that mode. It also mirrored the same photo on
// both faces of a turning page, which needed an on-screen disclaimer.
// Mobile's viewer (andiamo-places PhotoBookFlipViewer.js) was already a
// plain page-by-page viewer; this matches it, plus two-page spreads when
// the dialog is wide enough, so the desktop preview still reads as an open
// book. Takes the same plain `pages` array as before, so callers are
// unchanged.
//
// The real print page is 210mm square — 793.7px at the standard CSS
// reference-pixel ratio (1in = 96px), the same ratio Puppeteer's page.pdf()
// uses with no scale override (functions/index.js's page.pdf({width:"210mm",
// height:"210mm"}), confirmed 2026-10-03). Each page here is a much smaller
// square, which is fine for a photo page (its image uses width/height:100%)
// but NOT for a custom page's embedded content — an inserted image's width
// (HTMLInsertDialog's width picker) is an ABSOLUTE css px value
// (createImageHTML in Place.js), so it doesn't shrink with the page box.
// Custom pages are therefore rendered at their TRUE native size and the
// whole result scaled down with a CSS transform, so text reflow, image
// widths and the 12mm padding all scale together, matching what prints.
const PRINT_PAGE_PX = (210 / 25.4) * 96;
const PRINT_PADDING_PX = (12 / 25.4) * 96;

const MAX_PAGE = 320; // per-page square, matching the book's own 210mm x 210mm trim
const SPREAD_GAP = 2; // the "spine" between the two pages of a spread
// Smallest page that still shows as a spread (2026-10-17): narrower windows
// used to switch to one page at a time, hiding how the book pairs up.
const MIN_SPREAD_PAGE = 130;

const Page = ({ page, size }) => (
  <div className="pb-flip-page" style={{ width: size, height: size }}>
    {page.type === "blank" ? (
      // padPagesToEven's padding page — matches buildBookHtml's own blank
      // page exactly (an empty page, added when the real count is odd;
      // Peecho requires an even count for double-sided printing).
      <div className="pb-flip-html-page" />
    ) : page.type === "html" ? (
      <div className="pb-flip-html-page-scale-wrap">
        <div
          className="pb-flip-html-page-native"
          style={{
            width: PRINT_PAGE_PX,
            height: PRINT_PAGE_PX,
            padding: PRINT_PADDING_PX,
            transform: `scale(${size / PRINT_PAGE_PX})`,
          }}
          dangerouslySetInnerHTML={{ __html: page.captionHtml || "" }}
        />
      </div>
    ) : (
      <>
        <img src={page.src} alt="" className="pb-flip-img" style={{ objectPosition: page.objectPosition || "center" }} onError={(e) => { if (page.rawUrl && e.currentTarget.src !== page.rawUrl) e.currentTarget.src = page.rawUrl; }} />
        {page.captionHtml || page.caption || page.place?.Name ? (
          <div
            className="pb-flip-caption"
            dangerouslySetInnerHTML={{
              __html: page.captionHtml || page.caption || page.place?.Name || "",
            }}
          />
        ) : null}
      </>
    )}
  </div>
);

// The book as a list of "views": each view is the page indexes shown
// together. Spreads mimic a real bound book — the front and back covers
// stand alone, and interior pages pair up left/right (1+2, 3+4, …), which is
// how double-sided printing lays them out (Peecho's Hardcover/Softcover
// file-setup docs, confirmed 2026-10-02). Single mode is one page per view.
//
// Peecho's Hardcover layout (support article "Hardcover books: File set up
// guideline", read 2026-10-17): the PDF is front cover, content, back
// cover. Opening the cover shows its white inside and the white front of a
// binding sheet; turning that sheet shows its white back and PDF page 2 on
// the right. At the end, the second-to-last PDF page faces another white
// binding sheet, whose back faces the white inside of the back cover. None
// of these white sides is in the PDF or can be printed. The spreads used
// to pair 2+3, 4+5… and leave these out.
const WHITE = {
  "-1": "Inside of the front cover",
  "-2": "Binding sheet",
  "-3": "Binding sheet (back)",
  "-4": "Binding sheet",
  "-5": "Binding sheet (back)",
  "-6": "Inside of the back cover",
};
function buildViews(count, spreads) {
  if (count <= 0) return [];
  if (!spreads || count <= 2) return Array.from({ length: count }, (_, i) => [i]);
  const views = [[0], [-1, -2]];
  const lastInterior = count - 2;
  if (lastInterior >= 1) views.push([-3, 1]);
  for (let i = 2; i <= lastInterior; i += 2) views.push(i + 1 <= lastInterior ? [i, i + 1] : [i, -4]);
  if (lastInterior >= 1 && lastInterior % 2 === 1) views.push([-4]);
  views.push([-5, -6], [count - 1]);
  return views;
}

const WhitePage = ({ size, label }) => (
  <div className="pb-flip-page pb-flip-inside-cover" style={{ width: size, height: size }}>
    <span>{label}<br />white — not printed</span>
  </div>
);

function viewLabel(view, count) {
  const printed = view.filter((i) => i >= 0);
  if (printed.length === 0) return view[0] === -1 ? "Inside the front cover" : "Inside the back cover";
  if (view.length === 2 && printed.length === 1) return `Page ${printed[0] + 1}`;
  if (view.length === 1 && count > 2 && view[0] === 0) return "Front cover (outside)";
  if (view.length === 1 && count > 2 && view[0] === count - 1) return "Back cover (outside)";
  if (view.length === 1) return pageRoleLabel(view[0], count);
  return `Pages ${view[0] + 1}–${view[1] + 1}`;
}

class PhotoBookFlipViewer extends React.Component {
  bodyRef = React.createRef();
  state = { viewIndex: 0, availableWidth: MAX_PAGE * 2 + SPREAD_GAP };

  componentDidMount() {
    window.addEventListener("keydown", this._onKeyDown);
    // The modal's content is already mounted (shown on first render), so
    // measure now too — onEntered alone would leave a brief spread-sized
    // layout in a narrow window while the modal animates open.
    this._observeWidth();
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this._onKeyDown);
    this.resizeObserver?.disconnect();
  }

  // Measured once the modal has opened (onEntered) and on every resize, so
  // spread-vs-single and the page size follow the dialog's real width.
  _observeWidth = () => {
    const el = this.bodyRef.current;
    if (!el) return;
    const measure = () => {
      const style = getComputedStyle(el);
      const inner = el.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      if (inner > 0 && Math.abs(inner - this.state.availableWidth) >= 1) this._setWidth(inner);
    };
    measure();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(measure);
      this.resizeObserver.observe(el);
    }
  };

  // Keeps the same first page in view when switching between spread and
  // single mode (e.g. the window is resized mid-preview).
  _setWidth = (availableWidth) => {
    this.setState((prev) => {
      const count = this.props.pages?.length || 0;
      const firstPage = ((buildViews(count, this._spreads(prev.availableWidth))[prev.viewIndex] || [0]).filter((i) => i >= 0)[0]) ?? 0;
      const nextViews = buildViews(count, this._spreads(availableWidth));
      const viewIndex = Math.max(0, nextViews.findIndex((v) => v.includes(firstPage)));
      return { availableWidth, viewIndex };
    });
  };

  _spreads = (width) => width >= MIN_SPREAD_PAGE * 2 + SPREAD_GAP;

  _onEntered = (...args) => {
    this._observeWidth();
    this.props.onEntered?.(...args);
  };

  _onKeyDown = (e) => {
    if (!this.props.show) return;
    if (e.key === "ArrowLeft") this._go(-1);
    else if (e.key === "ArrowRight") this._go(1);
    else if (e.key === "Home") this._goTo(0);
    else if (e.key === "End") this._goTo(Infinity);
  };

  // Straight to a spread (covers, the slider) — 2026-10-17.
  _goTo = (index) => {
    const views = buildViews(this.props.pages?.length || 0, this._spreads(this.state.availableWidth));
    this.setState({ viewIndex: Math.min(views.length - 1, Math.max(0, index)) });
  };

  _go = (delta) => {
    const views = buildViews(this.props.pages?.length || 0, this._spreads(this.state.availableWidth));
    this.setState((prev) => ({ viewIndex: Math.min(views.length - 1, Math.max(0, prev.viewIndex + delta)) }));
  };

  render() {
    const { props } = this;
    const pages = props.pages || [];
    const spreads = this._spreads(this.state.availableWidth);
    const views = buildViews(pages.length, spreads);
    const viewIndex = Math.min(this.state.viewIndex, Math.max(0, views.length - 1));
    const view = views[viewIndex] || [];
    const pageSize = spreads
      ? Math.min(MAX_PAGE, Math.floor((this.state.availableWidth - SPREAD_GAP) / 2))
      : Math.min(MAX_PAGE, Math.floor(this.state.availableWidth));
    // The stage is always sized for the widest view in this mode, so the
    // dialog never changes size between a cover (one page) and a spread.
    const stageWidth = spreads ? pageSize * 2 + SPREAD_GAP : pageSize;
    // A cover stands alone, centred — nothing is beside it on a closed book
    // (an outlined "inside cover" half read as an extra page, 2026-10-17).
    const coverAlign = "center";

    return (
      <div className="modal">
        <Modal
          {...props}
          onEntered={this._onEntered}
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <Modal.Header>
            <div style={{ width: "100%" }}>
              <div className="dialog-heading">
                <div>
                  <h4 style={{ color: "#3598DB" }}>Preview</h4>
                </div>
                <div>
                  <FontAwesomeIcon
                    icon={solid("times")}
                    className="pointer color-blue"
                    onClick={() => props.onHide()}
                  ></FontAwesomeIcon>
                </div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div ref={this.bodyRef} className="pb-flip-body">
              {pages.length > 0 ? (
                <>
                  {/* Above the book, not overlaid on a page — a badge drawn
                      on the page image could read as something that will
                      actually print. Found 2026-10-02. */}
                  <div className="pb-flip-role-label-above">{viewLabel(view, pages.length)}</div>
                  <div
                    className="pb-flip-stage"
                    style={{ width: stageWidth, height: pageSize, justifyContent: coverAlign, gap: SPREAD_GAP }}
                  >
                    {/* key on the view so each turn re-mounts and fades in */}
                    <div className="pb-flip-view" key={`${spreads ? "s" : "p"}-${viewIndex}`} style={{ gap: SPREAD_GAP }}>
                      {view.map((i) =>
                        i < 0 ? (
                          <WhitePage key={i} size={pageSize} label={WHITE[i]} />
                        ) : (
                          <Page key={i} page={pages[i]} size={pageSize} />
                        )
                      )}
                    </div>
                  </div>
                  {views.length > 2 && (
                    <input
                      type="range"
                      className="pb-flip-scrub"
                      style={{ maxWidth: stageWidth }}
                      min={0}
                      max={views.length - 1}
                      value={viewIndex}
                      onChange={(e) => this._goTo(Number(e.target.value))}
                      aria-label="Go to page"
                    />
                  )}
                  <div className="pb-flip-nav" style={{ maxWidth: stageWidth }}>
                    <span className="pb-flip-nav-group">
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => this._goTo(0)} disabled={viewIndex === 0} title="Front cover (Home)">
                        « Front
                      </button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => this._go(-1)} disabled={viewIndex === 0}>
                        ‹ Previous
                      </button>
                    </span>
                    {/* Page numbers, not turns: with spreads one turn shows
                        two pages, so "4 / 32" read as page 4 when it was
                        pages 7–8. A page = one side, as the printer counts. */}
                    <span className="pb-flip-position">
                      {(() => {
                        const printed = view.filter((i) => i >= 0);
                        if (printed.length === 0) return `No printed pages · ${pages.length} in all`;
                        return `${printed.length > 1 ? `Pages ${printed[0] + 1}–${printed[1] + 1}` : `Page ${printed[0] + 1}`} of ${pages.length}`;
                      })()}
                    </span>
                    <span className="pb-flip-nav-group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => this._go(1)}
                        disabled={viewIndex >= views.length - 1}
                      >
                        Next ›
                      </button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => this._goTo(views.length - 1)} disabled={viewIndex >= views.length - 1} title="Back cover (End)">
                        Back »
                      </button>
                    </span>
                  </div>
                </>
              ) : (
                <div>No pages to preview.</div>
              )}
            </div>
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

export default PhotoBookFlipViewer;
